import React from 'react';
import type { CustomField as CustomFieldType } from '../../shared/custom_fields/types';
import type { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import {
  DatePickerField,
  SimpleFormMultiDropdown,
  SimpleFormSingleDropdown,
  SimpleFormTextField,
  SimpleTextAreaField,
  URL_REGEX,
} from './Fields';
import { Grid } from '@grnhse/seedling/lib/birch/layouts/grid';
import { useCandidateProfile } from '../../store';
import { standardRequest } from '../../../shared/utils/request';
import styled from 'styled-components';
import { withLocalePrefix } from '../../../shared/utils/translation';
import { BirchDatePickerFormat } from '../types';
const t = withLocalePrefix(
  'candidate_profile_redesign.candidate_details.custom_fields'
);

interface CustomFieldAttributes {
  value?: string | string[];
  unit?: string | string[];
}

interface CustomFieldValuesAttributes extends CustomFieldAttributes {
  custom_field_id: string;
  id?: string;
}

interface CustomFieldData {
  custom_field_values_attributes: {
    [key: string]: CustomFieldValuesAttributes;
  };
}

// This expects the pattern of custom-field-<index>-<custom_field_id>-<custom_field_value_id>-<attribute>
const EXISTING_CUSTOM_FIELD_REGEX =
  'custom-field-(\\d+)-(\\d+)-(\\d+)-(value|unit)';
const CUSTOM_FIELD_REGEX = 'custom-field-(\\d+)-(\\d+)-(value|unit)';

const regexMatcher = (
  fieldName: string
): {
  index: string;
  customFieldId: string;
  customFieldValueId?: string | null;
  attribute: keyof CustomFieldAttributes;
} | null => {
  const regex = new RegExp(CUSTOM_FIELD_REGEX);
  const existingCfRegex = new RegExp(EXISTING_CUSTOM_FIELD_REGEX);
  const matches = fieldName.match(existingCfRegex) ?? fieldName.match(regex);
  if (!matches) {
    return null;
  }

  if (matches.length === 5) {
    return {
      index: matches[1],
      customFieldId: matches[2],
      customFieldValueId: matches[3],
      attribute: matches[4] as keyof CustomFieldAttributes,
    };
  } else if (matches.length === 4) {
    return {
      index: matches[1],
      customFieldId: matches[2],
      attribute: matches[3] as keyof CustomFieldAttributes,
    };
  } else {
    return null;
  }
};

const CurrencyContainer = styled(Grid)`
  gap: 16px;
`;

//eslint-disable-next-line @typescript-eslint/no-explicit-any
export const serializeCustomFieldsPayload = (formData: any) => {
  const customFieldData: CustomFieldData = {
    custom_field_values_attributes: {},
  };
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  for (const [key, value] of Object.entries<any>(formData)) {
    const val = value?.value || value || null;
    const customFieldMatches = regexMatcher(key);
    if (customFieldMatches && val) {
      let fieldData =
        customFieldData.custom_field_values_attributes[
          customFieldMatches.index
        ];
      if (!fieldData) {
        fieldData = {
          custom_field_id: customFieldMatches.customFieldId,
        };
        // eslint-disable-next-line max-depth
        if (customFieldMatches.customFieldValueId) {
          fieldData.id = customFieldMatches.customFieldValueId;
        }
        customFieldData.custom_field_values_attributes[
          customFieldMatches.index
        ] = fieldData;
      }

      const existingValue = fieldData[customFieldMatches.attribute];
      // eslint-disable-next-line max-depth
      if (existingValue) {
        fieldData[customFieldMatches.attribute] = ([] as string[]).concat(
          existingValue,
          val
        );
      } else {
        fieldData[customFieldMatches.attribute] = Array.isArray(val)
          ? val.map((item) => item.value)
          : val;
      }
    }
  }
  return customFieldData;
};

const fetchItems = (path: string) => async (text: string) => {
  const result = await standardRequest.get(path, {
    params: { q: text, react_select: true },
  });

  const items = result.data.results;
  return { items };
};

interface CustomFieldProps {
  field: CustomFieldType;
  currencyOptions: Item[];
  showFieldNameLabel?: boolean;
  index?: number;
  dateFormatBirchPicker: BirchDatePickerFormat;
  userOptionsPath: string;
}
const CustomField = ({
  field,
  currencyOptions,
  showFieldNameLabel,
  index,
  dateFormatBirchPicker,
  userOptionsPath,
}: CustomFieldProps) => {
  const getUserItems = fetchItems(userOptionsPath);

  let component;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rules: Record<string, any> = {};
  if (field.required) {
    rules.required = t('required');
  }

  let fieldId;
  if (field.existing_value_id) {
    fieldId = `${index}-${field.id}-${field.existing_value_id}`;
  } else {
    fieldId = `${index}-${field.id}`;
  }

  const customFieldValuesAttributesPrefix = `custom-field-${fieldId}-`;

  switch (field.value_type) {
    case 'multi_select': {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const getItems = fetchItems(field.options_url);

      component = (
        <SimpleFormMultiDropdown
          items={getItems}
          name={`${customFieldValuesAttributesPrefix}value`}
          label={showFieldNameLabel && field.name}
          ariaLabel={field.name}
          rules={rules}
        />
      );
      break;
    }
    case 'user':
      component = (
        <SimpleFormSingleDropdown
          items={getUserItems}
          name={`${customFieldValuesAttributesPrefix}value`}
          label={showFieldNameLabel && field.name}
          ariaLabel={field.name}
          rules={rules}
        />
      );
      break;
    case 'single_select': {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const getItems = fetchItems(field.options_url);

      component = (
        <SimpleFormSingleDropdown
          items={getItems}
          name={`${customFieldValuesAttributesPrefix}value`}
          label={showFieldNameLabel && field.name}
          ariaLabel={field.name}
          rules={rules}
        />
      );
      break;
    }
    case 'yes_no':
      component = (
        <SimpleFormSingleDropdown
          items={field.custom_field_options ?? []}
          name={`${customFieldValuesAttributesPrefix}value`}
          label={showFieldNameLabel && field.name}
          ariaLabel={field.name}
          rules={rules}
        />
      );
      break;
    case 'url':
      //eslint-disable-next-line @typescript-eslint/no-explicit-any
      rules.validate = (val: any) => {
        if (!val) {
          return null;
        }

        return URL_REGEX.test(val) ? null : t('url');
      };
      component = (
        <SimpleFormTextField
          id={`custom-field-${field.id}`}
          name={`${customFieldValuesAttributesPrefix}value`}
          label={showFieldNameLabel && field.name}
          ariaLabel={field.name}
          rules={rules}
        />
      );
      break;
    case 'number':
      component = (
        <SimpleFormTextField
          id={`custom-field-${field.id}`}
          name={`${customFieldValuesAttributesPrefix}value`}
          label={showFieldNameLabel && field.name}
          ariaLabel={field.name}
          rules={rules}
          type="number"
        />
      );
      break;
    case 'short_text':
      component = (
        <SimpleFormTextField
          id={`custom-field-${field.id}`}
          name={`${customFieldValuesAttributesPrefix}value`}
          label={showFieldNameLabel && field.name}
          ariaLabel={field.name}
          rules={rules}
        />
      );
      break;
    case 'long_text':
      component = (
        <SimpleTextAreaField
          id={`custom-field-${field.id}`}
          name={`${customFieldValuesAttributesPrefix}value`}
          label={showFieldNameLabel && field.name}
          ariaLabel={field.name}
          rules={rules}
        />
      );
      break;
    case 'currency':
      component = (
        <CurrencyContainer container spacing={0} direction="row">
          {/* @ts-expect-error - missing style props */}
          <Grid item style={{ flex: 0.5 }}>
            <SimpleFormSingleDropdown
              items={currencyOptions}
              name={`${customFieldValuesAttributesPrefix}unit`}
              label={t('currency')}
              rules={rules}
            />
          </Grid>
          {/* @ts-expect-error - missing style prop */}
          <Grid item style={{ flex: 1 }}>
            <SimpleFormTextField
              id={`custom-field-${field.id}`}
              name={`${customFieldValuesAttributesPrefix}value`}
              label={showFieldNameLabel ? field.name : t('amount')}
              rules={rules}
            />
          </Grid>
        </CurrencyContainer>
      );
      break;
    case 'date':
      component = (
        <DatePickerField
          name={`${customFieldValuesAttributesPrefix}value`}
          dateFormat={dateFormatBirchPicker}
          label={showFieldNameLabel ? field.name : ''}
          ariaLabelledBy={field.name}
          rules={rules}
          fullWidth
        />
      );
      break;
    default:
      component = null;
      break;
  }

  return <div data-provides={`${field.name}-field`}>{component}</div>;
};

export default CustomField;
